import { useEffect, useState } from "react"

import {
  FEATURE_FLAGS,
  LOCAL_DEV_FLAGS,
  shouldUseLocalDevFeatureFlagOverrides,
  useOrganizationFeatureFlagScope,
  usePostHydrationFeatureFlagValue,
} from "@/feature-flags"

const WORKSPACE_FLAG_FALLBACK_MS = 2_000

function isPostHogFeatureFlagsEnabled(): boolean {
  return (
    process.env.NODE_ENV === "production" ||
    process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true"
  )
}

function isLocalWorkspaceFlagOverrideConfigured(): boolean {
  return (
    shouldUseLocalDevFeatureFlagOverrides() &&
    FEATURE_FLAGS.WORKSPACES in LOCAL_DEV_FLAGS
  )
}

export function useWorkspaceFeatureFlag() {
  const workspaceFlagValue = usePostHydrationFeatureFlagValue(
    FEATURE_FLAGS.WORKSPACES,
  )
  const { organizationId, isReady: isFeatureFlagScopeReady } =
    useOrganizationFeatureFlagScope()
  const featureFlagsEnabled =
    isPostHogFeatureFlagsEnabled() || isLocalWorkspaceFlagOverrideConfigured()
  const [workspaceFlagResolution, setWorkspaceFlagResolution] = useState<{
    organizationId: string | null
    value: boolean | undefined
  }>({
    organizationId,
    value: undefined,
  })
  const resolvedWorkspaceFlagValue =
    workspaceFlagResolution.organizationId === organizationId
      ? workspaceFlagResolution.value
      : undefined
  const canResolveWorkspaceFlag =
    organizationId !== null && isFeatureFlagScopeReady

  useEffect(() => {
    if (!featureFlagsEnabled || organizationId === null) {
      return
    }

    if (canResolveWorkspaceFlag && workspaceFlagValue !== undefined) {
      setWorkspaceFlagResolution({
        organizationId,
        value: workspaceFlagValue,
      })
      return
    }

    if (resolvedWorkspaceFlagValue !== undefined) {
      return
    }

    const timeout = window.setTimeout(() => {
      setWorkspaceFlagResolution({ organizationId, value: false })
    }, WORKSPACE_FLAG_FALLBACK_MS)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [
    canResolveWorkspaceFlag,
    featureFlagsEnabled,
    organizationId,
    resolvedWorkspaceFlagValue,
    workspaceFlagValue,
  ])

  const isWorkspaceFlagResolved =
    !featureFlagsEnabled ||
    organizationId === null ||
    resolvedWorkspaceFlagValue !== undefined
  const isWorkspacesEnabled =
    featureFlagsEnabled &&
    isWorkspaceFlagResolved &&
    resolvedWorkspaceFlagValue === true

  return {
    isWaitingForWorkspaceFlag:
      featureFlagsEnabled &&
      organizationId !== null &&
      !isWorkspaceFlagResolved,
    isWorkspaceFlagResolved,
    isWorkspacesEnabled,
    organizationId,
  }
}
